import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { LOCALE_OPTIONS } from '@link/share/constants';
import { useSupportedLanguageCodes } from '@link/share/hooks';
import { BaseLayout } from '@link/share/layouts';
import { LanguageCodeType } from '@link/share/services/types';
import { buildLocaleList, updateThemeColor } from '@link/share/utils';
import Logo from '@link/shibuya-sky/assets/shibuya-sky.png';
import { useStore } from '@link/shibuya-sky/stores';
import shibuyaSkyAuth from '@link/shibuya-sky/vendors/firebase';

const ShibuyaSkyLayout = observer(() => {
  const { i18n } = useTranslation();
  const { auth, site, account, product, cart } = useStore();

  useEffect(() => {
    site.getSiteSchema(i18n.language.toUpperCase() as LanguageCodeType);
  }, [i18n.language, site]);

  useEffect(() => {
    updateThemeColor(site.siteSchema?.siteLayout?.primaryColor, {
      disabledColor: 'var(--color-disabled-bg)'
    });
  }, [site.siteSchema?.siteLayout?.primaryColor]);

  useEffect(() => {
    if (!auth.accessToken) {
      cart.clear();
      return;
    }
    cart.getCart();
  }, [auth.accessToken, cart]);

  const logout = useCallback(async () => {
    await shibuyaSkyAuth.logout();
    cart.clear();
    account.setAccountInfo({});
  }, [account, cart]);

  const { supportedLanguageCodes } = useSupportedLanguageCodes(
    site.supportedLanguageCodes,
    product.product.supportedLanguageCodes
  );

  const logoUrl = useMemo(() => {
    return site.logoUrl ?? Logo;
  }, [site.logoUrl]);

  const localeList = useMemo(
    () => buildLocaleList(LOCALE_OPTIONS, supportedLanguageCodes),
    [supportedLanguageCodes]
  );

  return (
    <BaseLayout
      appLogo={logoUrl}
      logout={logout}
      loggedIn={auth.loggedIn}
      isPasswordAuth={auth.isPasswordAuth}
      appTitle="SHIBUYA SKY"
      showCurrency={site.showOtherCurrencies}
      supportedCurrencies={site.supportedCurrencies}
      supportCart={site.supportCart}
      cartStore={cart}
      currentCurrency={site.currency}
      onCurrencyChange={currency => site.setCurrency(currency)}
      localeList={localeList}
    />
  );
});

export { ShibuyaSkyLayout };
